import { useState, ReactElement } from 'react'
import { chunk } from 'lodash'

import {
  selectOtherLocations,
  selectPayrollCompany,
} from '../payroll.selectors'
import { LocationsPanel } from './LocationsPanel'
import { WorkAddressModal } from './WorkAddressModal'
import { Link, Text, Button, Alert, Separator, Icon } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { faMapMarkerAlt } from '@fortawesome/pro-regular-svg-icons'
import { TypographyStyles } from '@/styles'

// This splits columns into multiple rows
const LocationRows = ({ subComps }: { subComps: ReactElement[] }) => {
  const rows = chunk(subComps, 4)

  return (
    <>
      {rows.map((row, index) => (
        <div key={index} className="mt-4">
          {row}
        </div>
      ))}
    </>
  )
}

// This is for both signup and settings
export const CompanyLocationsTab = () => {
  const [showModal, setShowModal] = useState(false)
  const otherLocations = useReselector(selectOtherLocations)
  const company = useReselector(selectPayrollCompany)

  if (!company) {
    return null
  }

  return (
    <div>
      <div className="flex flex-col gap-6">
        <Alert>
          This holds information about where your company and your employees are
          situated. This is tricky — especially with WFH — as we need the
          correct information in order to correctly pay your payroll taxes.
          Please keep this up to date.
        </Alert>

        <LocationsPanel title="Main Company Locations" />

        <Text variant={TypographyStyles.BODY_SM}>
          Need to edit this? This may have payroll tax consequences.{' '}
          <Link href="/conversations">Contact the Heard Team</Link>
        </Text>

        <Separator />
        <div className="flex flex-row items-center ">
          <Icon icon={faMapMarkerAlt} className="inline-block mr-2" />

          <Text variant={TypographyStyles.H3}>Other Company Locations</Text>
        </div>

        <Text variant={TypographyStyles.BODY_SM}>
          This includes employees’ remote work addresses. This is needed as we
          need to know where your employees work, in order to correctly file
          your payroll taxes.
        </Text>
      </div>

      <LocationRows
        subComps={[
          ...otherLocations.map(
            ({ uuid, street_1, street_2, city, state }, index) => (
              <div className="w-full md:w-1/4" key={uuid}>
                <div className="border rounded-lg p-4 h-full flex flex-col gap-2">
                  <div>
                    <Text>
                      <b>Location #{index + 1}</b>
                    </Text>
                  </div>
                  <div>
                    <Text>
                      {street_1} {street_2 || ''}
                    </Text>
                  </div>
                  <div>
                    <Text>
                      {city}, {state}
                    </Text>
                  </div>
                </div>
              </div>
            )
          ),
          <div className="w-full md:w-1/4" key="addNew">
            <div className="border border-dashed rounded-lg p-6 h-full flex items-center justify-center">
              <Button
                onClick={() => setShowModal(true)}
                variant="link"
                size="sm"
              >
                Add a location
              </Button>
            </div>
          </div>,
        ]}
      />
      {showModal && (
        <WorkAddressModal
          closeModal={() => setShowModal(false)}
          open={showModal}
        />
      )}
    </div>
  )
}
